// https://usehooks.com/useAsync/
import { useState, useCallback, useEffect } from 'react'

export type Status = 'idle' | 'pending' | 'success' | 'error'

export type UseAsyncConfig<T> =
  | { immediate: true; valueBeforeAsyncRun: null }
  | { immediate: false; valueBeforeAsyncRun: T }

export const useAsync = <T, E = unknown>(
  asyncFunction: () => Promise<T>,
  { immediate = true, valueBeforeAsyncRun = null }: UseAsyncConfig<T>
): {
  execute: () => Promise<void>
  status: Status
  value: T | null
  error: E | null
} => {
  const [status, setStatus] = useState<Status>('idle')
  const [value, setValue] = useState<T | null>(valueBeforeAsyncRun)
  const [error, setError] = useState<E | null>(null)

  // The execute function wraps asyncFunction and
  // handles setting state for pending, value, and error.
  // useCallback ensures the below useEffect is not called
  // on every render, but only if asyncFunction changes.
  const execute = useCallback(() => {
    setStatus('pending')
    setValue(null)
    setError(null)
    return asyncFunction()
      .then((response: T) => {
        setValue(response)
        setStatus('success')
      })
      .catch((caughtError: E) => {
        setError(caughtError)
        setStatus('error')
      })
  }, [asyncFunction])

  // Call execute if we want to fire it right away.
  // Otherwise execute can be called later, such as
  // in an onClick handler.
  useEffect(() => {
    if (immediate) {
      execute()
    }
  }, [execute, immediate])
  return { execute, status, value, error }
}
